import React, { useEffect } from 'react'
import { Box, Typography, Tooltip } from '@material-ui/core'
import styled from '@emotion/styled'
import { alpha } from '@material-ui/core/styles'
import MessageInput from './MessageInput'
import QuickAccessBar from './QuickAccessBar'
import { useNavbarContext } from 'src/contexts/NavbarContext'
import { useHpo } from 'src/api/vcf'
import PriorityHighIcon from '@mui/icons-material/PriorityHigh'
import useChat from 'src/components/chatbot/hooks/useChat'

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  height: 100%;
  gap: ${({ theme }) => theme.spacing(2)};
  padding-top: 15%;
`

const WelcomeMessage = styled(Typography)`
  text-align: center;
  color: ${({ theme }) => theme.palette.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`

const SuggestedQueriesContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  width: 80%;
  margin-top: ${({ theme }) => theme.spacing(4)};
`

const SuggestedQueryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;
  padding: ${({ theme }) => theme.spacing(1, 1.5)};
  border-radius: 8px;
  background-color: ${({ theme, disabled }) => alpha(theme.palette.text.primary, disabled ? 0.02 : 0.05)};
  width: 100%;
  border: none;
  font-size: 0.85rem;
  color: ${({ theme, disabled }) => (disabled ? theme.palette.text.disabled : theme.palette.text.primary)};
  opacity: ${({ disabled }) => (disabled ? 0.7 : 1)};

  &:hover {
    background-color: ${({ theme, disabled }) => alpha(theme.palette.text.primary, disabled ? 0.02 : 0.09)};
  }
`

const SuggestedQueryText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
`

const SuggestedQueryLabel = styled(Typography)`
  color: ${({ theme }) => theme.palette.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`

const WarningMessage = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  color: ${({ theme }) => theme.palette.warning.main};
  font-size: 0.75rem;
  margin-top: ${({ theme }) => theme.spacing(2)};
  text-align: center;
`

const WarningIcon = styled(PriorityHighIcon)`
  font-size: 16px;
  color: ${({ theme }) => theme.palette.warning.main};
  margin-left: ${({ theme }) => theme.spacing(0.5)};
`

export default function ChatEmptyState({
  isDarkMode,
  selectedHPOTerms,
  onRemoveHPOTerm,
  selectedDiseases,
  onRemoveDisease,
  selectedSamples,
  onRemoveSample,
  selectedPanels,
  onRemovePanel,
  isVariantsTablePage,
  suggestedQueries,
  onHPOButtonClick,
  onDiseaseButtonClick,
  onSamplesButtonClick,
  onPanelsButtonClick,
}) {
  const { fileId } = useNavbarContext()
  const [hpoList] = useHpo({ fileId })
  const { handleSendMessage } = useChat()

  const queriesThatRequireHpo = [
    'Filter for homozygous or compound heterozygous variants in genes known to be associated with this patients phenotypes',
    "Show me variants in ClinVar with a 'pathogenic' or 'likely pathogenic' assertion that match the patient's phenotypes.",
    'Filter variants by phenotype association and show only those with high pathogenicity scores',
    'Show me variants with ACMG classification of pathogenic or likely pathogenic associated with patients phenotype',
    'Filter for rare variants (MAF < 1%) in genes known to be associated with patients phenotype with a high predicted impact (e.g., CADD score > 20).',
  ]

  const isHpoListEmpty = !hpoList || hpoList.length === 0

  return (
    <EmptyStateContainer>
      <WelcomeMessage variant="body1">How can I help you today?</WelcomeMessage>
      <Box sx={{ width: '80%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <QuickAccessBar
            isDarkMode={isDarkMode}
            onHPOButtonClick={onHPOButtonClick}
            onDiseaseButtonClick={onDiseaseButtonClick}
            onSamplesButtonClick={onSamplesButtonClick}
            onPanelsButtonClick={onPanelsButtonClick}
          />
          <MessageInput
            selectedHPOTerms={selectedHPOTerms}
            onRemoveHPOTerm={onRemoveHPOTerm}
            selectedDiseases={selectedDiseases}
            onRemoveDisease={onRemoveDisease}
            selectedSamples={selectedSamples}
            onRemoveSample={onRemoveSample}
            selectedPanels={selectedPanels}
            onRemovePanel={onRemovePanel}
          />
        </Box>
        {/* Warning message when HPO list is empty */}
        {isVariantsTablePage && isHpoListEmpty && (
          <WarningMessage>
            <PriorityHighIcon style={{ fontSize: '16px' }} />
            <Typography variant="caption">Some queries require you to associate HPO</Typography>
          </WarningMessage>
        )}
      </Box>

      {/* suggested queries section */}
      {isVariantsTablePage && suggestedQueries && suggestedQueries.length > 0 && (
        <SuggestedQueriesContainer>
          <SuggestedQueryLabel variant="caption">Suggested queries:</SuggestedQueryLabel>
          {suggestedQueries.map((query, index) => {
            const requiresHpo = queriesThatRequireHpo.includes(query)
            const showWarningIcon = requiresHpo && isHpoListEmpty
            const isDisabled = showWarningIcon

            return (
              <Tooltip key={index} title={query} placement="bottom-start">
                <SuggestedQueryButton
                  isDarkMode={isDarkMode}
                  disabled={isDisabled}
                  onClick={() => {
                    if (!isDisabled) {
                      handleSendMessage(query)
                    }
                  }}
                >
                  <SuggestedQueryText>{query}</SuggestedQueryText>
                  {showWarningIcon && <WarningIcon />}
                </SuggestedQueryButton>
              </Tooltip>
            )
          })}
        </SuggestedQueriesContainer>
      )}
    </EmptyStateContainer>
  )
}
